import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { catalogApi } from '../lib/api'
import { db } from '../lib/db'
import { useApp } from '../lib/store'

/**
 * Tích chọn bộ ghi chú cho một món, ngay trong form sửa món.
 *
 * Thư viện bộ dùng chung cả quán: quán trà sữa nào cũng cần "ít đường", khai một lần rồi
 * tích vào những món dùng tới. Bộ chưa có thì thêm ngay tại đây — bắt người ta thoát ra,
 * vào một màn hình cài đặt khác, rồi quay lại đúng cái món đang sửa dở là mất luôn cái
 * mạch đang làm.
 */
export function OptionSetPicker({
  value,
  onChange,
}: {
  value: string[]
  onChange: (ids: string[]) => void
}) {
  const say = useApp((s) => s.say)
  const refreshCatalog = useApp((s) => s.refreshCatalog)

  const sets = useLiveQuery(() => db.optionSets.orderBy('sortOrder').toArray(), [], [])

  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [choices, setChoices] = useState('')
  const [busy, setBusy] = useState(false)

  const picked = new Set(value)

  function toggle(id: string) {
    const next = new Set(picked)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange([...next])
  }

  async function addSet() {
    const list = choices
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean)

    if (!name.trim()) {
      say('Chưa đặt tên bộ')
      return
    }
    if (list.length === 0) {
      say('Chưa có lựa chọn nào')
      return
    }

    setBusy(true)
    try {
      const made = await catalogApi.saveOptionSet({ name: name.trim(), choices: list })
      await refreshCatalog()

      // Bộ vừa thêm thì tích luôn cho món đang sửa — người ta thêm nó chính vì món này.
      onChange([...picked, made.id])

      setName('')
      setChoices('')
      setAdding(false)
      say(`Đã thêm bộ ${made.name}`)
    } catch (e) {
      say(e instanceof Error ? e.message : 'Chưa thêm được')
    }
    setBusy(false)
  }

  return (
    <div className="field">
      <span>Ghi chú đặt sẵn cho món này</span>

      {sets.length === 0 ? (
        <p className="note">Quán chưa có bộ ghi chú nào. Thêm một bộ ở dưới.</p>
      ) : (
        <div className="opt-sets">
          {sets.map((s) => (
            <label key={s.id} className={`opt-set${picked.has(s.id) ? ' is-on' : ''}`}>
              <input
                type="checkbox"
                checked={picked.has(s.id)}
                onChange={() => toggle(s.id)}
              />
              <span className="opt-set-text">
                <b>{s.name}</b>
                <small>{s.choices.join(' · ')}</small>
              </span>
            </label>
          ))}
        </div>
      )}

      {adding ? (
        <div className="opt-new">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Tên bộ, ví dụ: Đường"
            maxLength={60}
            autoFocus
          />
          <input
            value={choices}
            onChange={(e) => setChoices(e.target.value)}
            placeholder="Các lựa chọn, cách nhau bằng dấu phẩy"
            maxLength={400}
          />
          <p className="note">Ví dụ: Ít đường, Bình thường, Nhiều đường</p>
          <div className="sheet-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setAdding(false)}>
              Thôi
            </button>
            <button type="button" className="btn btn-brand grow" onClick={addSet} disabled={busy}>
              {busy ? 'Đang thêm…' : 'Thêm bộ này'}
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="btn btn-ghost wide" onClick={() => setAdding(true)}>
          + Thêm bộ ghi chú mới
        </button>
      )}
    </div>
  )
}
